import React from "react";

interface HttpStatus {
  code: string;
  meaning: string;
}

interface Category {
  range: string;
  category: string;
  action: string;
}

interface ErrorCode {
  code: string;
  message: string;
}

interface CategoryGroup {
  range: string;
  title: string;
  codes: ErrorCode[];
}

const HTTP_STATUSES: HttpStatus[] = [
  { code: "200", meaning: "Success. errorCode is null." },
  { code: "400", meaning: "Validation or business rule failure; errorCode identifies it." },
  {
    code: "401",
    meaning:
      "Missing, invalid or expired Bearer token. The body is empty (no envelope, no errorCode, no X-Request-Id). Request a new token with Get Token.",
  },
  {
    code: "403",
    meaning:
      "Token accepted, but the request did not come from an IP address whitelisted for this user. errorCode DPI-6003; the keys of this response are PascalCase.",
  },
  {
    code: "404",
    meaning:
      "Returned by Get Token when the user does not exist or is inactive; errorCode is null. An unknown URL also returns 404 with an empty body.",
  },
  {
    code: "429",
    meaning: "Rate limit exceeded. errorCode is null; retry after the Retry-After header (60 seconds).",
  },
  {
    code: "500",
    meaning:
      "Unexpected server error (DPI-9001), or a Send Remittance processing failure (DPI-3004, DPI-3005). Read errorCode before retrying: DPI-3004 needs corrected sender and receiver country details; the others may be retried after a delay (see DPI-9xxx).",
  },
];

export const DPI_CATEGORIES: Category[] = [
  {
    range: "DPI-1xxx",
    category: "Request validation",
    action:
      "Do not retry unchanged; correct the request and resubmit. The requestUniqueId is released when a send is rejected at this stage, so the same value may be reused. Exception: DPI-1028 means the Edahab name check could not be completed; retry the same request after a short delay.",
  },
  {
    range: "DPI-2xxx",
    category: "Account validation",
    action:
      "DPI-2001 and DPI-2004 to DPI-2006: the request is malformed; correct it. DPI-2007: the wallet does not exist; do not retry, correct the account at source. DPI-2002 / DPI-2003: the lookup could not be confirmed. First check that entity and type are a valid pair (see the Appendix); if they are, retry two or three times with a short delay, then contact support quoting the X-Request-Id.",
  },
  {
    range: "DPI-3xxx",
    category: "Funds / pricing",
    action:
      "Retry once the underlying condition is resolved (balance topped up, correct commission, supported currency). On Send Remittance the requestUniqueId is released, so the corrected request may reuse it. DPI-3004 and DPI-3005 are returned with HTTP 500.",
  },
  {
    range: "DPI-4xxx",
    category: "Transaction state",
    action:
      "Do not retry. The transaction is in a state that does not allow the operation.",
  },
  {
    range: "DPI-5xxx",
    category: "Duplicate request",
    action:
      "Do not resubmit with the same requestUniqueId. If the original outcome is unknown, query Get Transaction Details first.",
  },
  {
    range: "DPI-6xxx",
    category: "Authorization",
    action: "Do not retry. Verify credentials and IP whitelisting with our team.",
  },
  {
    range: "DPI-9xxx",
    category: "System error",
    action:
      "Safe to retry after a delay. On Send Remittance a DPI-9xxx failure does not release the requestUniqueId (it stays reserved for 10 minutes) and the transaction may or may not have been created: query Get Transaction Details with your origRefNumber first, then resubmit under a new requestUniqueId. If it persists, contact support with the X-Request-Id value.",
  },
];

export const DPI_ERROR_CODES: CategoryGroup[] = [
  {
    range: "DPI-1xxx",
    title: "Request validation",
    codes: [
      {
        code: "DPI-1000",
        message:
          "Generic or unclassified validation failure: a missing username or password on Get Token, a missing query parameter on a GET endpoint, an empty request body, a property missing from a POST body, or a destination-agent rule whose field is not in this list",
      },
      { code: "DPI-1001", message: "Request Unique ID is required" },
      { code: "DPI-1002", message: "Original Reference Number is required" },
      { code: "DPI-1003", message: "Amount is required" },
      { code: "DPI-1004", message: "Amount must be greater than 0" },
      { code: "DPI-1005", message: "Payment Method is required" },
      {
        code: "DPI-1006",
        message:
          "A field could not be read in the format expected (invalid enum value, non-numeric amount, malformed date); message carries the raw deserialization error",
      },
      { code: "DPI-1010", message: "Customer ID is required / Sender ID is required" },
      { code: "DPI-1011", message: "Sender Name is required" },
      {
        code: "DPI-1012",
        message: "Sender Phone is required (only when the destination agent requires it; otherwise optional)",
      },
      { code: "DPI-1013", message: "Sender Phone must be between 7 and 15 characters long" },
      { code: "DPI-1014", message: "Sender Document Type is required" },
      { code: "DPI-1015", message: "Invalid Sender Document Type" },
      { code: "DPI-1016", message: "Sender City is required" },
      { code: "DPI-1017", message: "Sender Address is required" },
      { code: "DPI-1018", message: "Sender Country is required" },
      { code: "DPI-1019", message: "Sender Document Number is required" },
      {
        code: "DPI-1020",
        message: "Receiver ID is required (only when the destination agent requires it; otherwise optional)",
      },
      { code: "DPI-1021", message: "Receiver Name is required" },
      { code: "DPI-1022", message: "Receiver Phone is required" },
      { code: "DPI-1023", message: "Receiver Phone must be between 7 and 15 characters long" },
      {
        code: "DPI-1024",
        message:
          "Receiver Nationality is required / Receiver Nationality is required when a commission amount is provided",
      },
      { code: "DPI-1025", message: "Destination Agent is required" },
      { code: "DPI-1026", message: "Destination Agent code must be 3 characters long" },
      {
        code: "DPI-1027",
        message: "Receiver name does not match the registered Edahab account name",
      },
      {
        code: "DPI-1028",
        message:
          "Unable to verify the receiver's Edahab account, please try again (transient; retry after a short delay)",
      },
      { code: "DPI-1030", message: "Bank Details are required for Bank Deposit payment method" },
      { code: "DPI-1031", message: "Bank Account ID is required" },
      { code: "DPI-1040", message: "Sender Issue Date is required" },
      { code: "DPI-1041", message: "Sender Expire Date is required" },
      { code: "DPI-1042", message: "Sender Date of Birth is required" },
      { code: "DPI-1050", message: "Sender is required" },
      { code: "DPI-1051", message: "Receiver is required" },
      { code: "DPI-1060", message: "TTNO is required / Transaction ID is required" },
      { code: "DPI-1061", message: "Beneficiary Name is required" },
      { code: "DPI-1062", message: "Beneficiary Mobile is required" },
    ],
  },
  {
    range: "DPI-2xxx",
    title: "Account validation",
    codes: [
      { code: "DPI-2001", message: "Invalid account type" },
      {
        code: "DPI-2002",
        message:
          "mobile validation failed (the wallet lookup could not be confirmed, or entity/type is not a valid wallet pair)",
      },
      {
        code: "DPI-2003",
        message:
          "account validation failed (the bank lookup could not be confirmed, or entity/type is not a valid bank pair)",
      },
      { code: "DPI-2004", message: "MobileNo is required (account is missing or empty)" },
      { code: "DPI-2005", message: "Entity is required" },
      { code: "DPI-2006", message: "Type is required" },
      { code: "DPI-2007", message: "Account not found (the Edahab wallet does not exist)" },
    ],
  },
  {
    range: "DPI-3xxx",
    title: "Funds / pricing",
    codes: [
      { code: "DPI-3001", message: "Insufficient balance to process this remittance" },
      { code: "DPI-3002", message: "Insufficient credit limit to process this remittance" },
      { code: "DPI-3003", message: "The commission amount provided is incorrect" },
      {
        code: "DPI-3004",
        message:
          "Unable to determine countries, please check sender and receiver details (returned with HTTP 500)",
      },
      {
        code: "DPI-3005",
        message:
          "Unable to calculate commission for this transaction (quotes the Request ID; returned with HTTP 500)",
      },
      { code: "DPI-3006", message: "Currency rates not found" },
    ],
  },
  {
    range: "DPI-4xxx",
    title: "Transaction state",
    codes: [
      { code: "DPI-4001", message: "Transaction is not ready for modification" },
      { code: "DPI-4002", message: "Transaction not found" },
      { code: "DPI-4003", message: "Invalid RefundType / RefundType is required" },
      { code: "DPI-4004", message: "Transaction cancellation failed" },
      { code: "DPI-4005", message: "Failed to update beneficiary details" },
    ],
  },
  {
    range: "DPI-5xxx",
    title: "Duplicate request",
    codes: [
      {
        code: "DPI-5001",
        message: "Duplicate request detected. Please do not resubmit the same request.",
      },
    ],
  },
  {
    range: "DPI-6xxx",
    title: "Authorization",
    codes: [
      {
        code: "DPI-6001",
        message:
          "Invalid input (the authenticated user could not be resolved to an agent account; Get Balance, Cancel Remittance, Get Transaction Details, Update Beneficiary Details)",
      },
      { code: "DPI-6002", message: "Invalid user (the same condition on Send Remittance)" },
      { code: "DPI-6003", message: "User is not authorized (HTTP 403: IP address not whitelisted for the user)" },
    ],
  },
  {
    range: "DPI-9xxx",
    title: "System errors",
    codes: [
      {
        code: "DPI-9001",
        message:
          "An unexpected error occurred (quotes the Request ID) / Internal Server Error",
      },
      { code: "DPI-9002", message: "Failed to process remittance (HTTP 500)" },
    ],
  },
];

interface Props {
  /**
   * Set to false on endpoints whose business failures carry `errorCode` null
   * (currently Get Token). The HTTP status table is still rendered.
   */
  showErrorCodes?: boolean;
}

function ErrorCodes({ showErrorCodes = true }: Props) {
  return (
    <div className="my-5">
      <h4 className="my-3">Error Codes</h4>

      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
        Every response uses the same envelope:{" "}
        <code>{`{ statusCode, message, errorCode, data }`}</code>. On success{" "}
        <code>errorCode</code> is <code>null</code>. On failure it carries a stable{" "}
        <code>DPI-xxxx</code> value.{" "}
        <strong>
          Branch on <code>errorCode</code>, not on <code>message</code>
        </strong>{" "}
        &mdash; message wording may change, codes will not.
      </p>

      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
        Parse the envelope keys case-insensitively: 403 responses and unexpected-error
        500 responses are emitted as <code>StatusCode / Message / ErrorCode / Data</code>.
        When several fields fail validation at once, <code>message</code> lists every
        failure separated by <code>, </code> (or <code>; </code> for Send Remittance
        pre-checks) and <code>errorCode</code> reflects the first; on these
        request-validation failures <code>data</code> is the string{" "}
        <code>"One or more validation errors occurred."</code> rather than{" "}
        <code>null</code>.
      </p>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th className="text-nowrap">HTTP Status</th>
              <th>Meaning</th>
            </tr>
          </thead>
          <tbody>
            {HTTP_STATUSES.map((status) => (
              <tr key={status.code}>
                <td className="text-nowrap">{status.code}</td>
                <td>{status.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showErrorCodes ? (
        <>
          <div className="alert alert-warning text-14">
            <strong>Two exceptions.</strong> <code>errorCode</code> is <code>null</code> on{" "}
            <strong>429 Rate limit exceeded</strong> responses and on the{" "}
            <strong>Get Token</strong> 404 (user not found or inactive). Branch on the
            HTTP status for those two cases. A <strong>401</strong> has no body at all.
            Get Token's own validation failures do carry DPI-1000 / DPI-1006.
          </div>

          <h5 className="mt-5 mb-3">Categories and recommended action</h5>
          <p className="text-muted text-14">
            The first digit after <code>DPI-</code> identifies the category, which
            determines the recommended action.
          </p>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th className="text-nowrap">Range</th>
                  <th className="text-nowrap">Category</th>
                  <th>Recommended action</th>
                </tr>
              </thead>
              <tbody>
                {DPI_CATEGORIES.map((category) => (
                  <tr key={category.range}>
                    <td className="text-nowrap">{category.range}</td>
                    <td className="text-nowrap">{category.category}</td>
                    <td>{category.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h5 className="mt-5 mb-3">Full code list</h5>
          {DPI_ERROR_CODES.map((group) => (
            <div className="mb-4" key={group.range}>
              <h6 className="mb-2">
                {group.range} &mdash; {group.title}
              </h6>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th className="text-nowrap">Code</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.codes.map((error) => (
                      <tr key={error.code}>
                        <td className="text-nowrap">{error.code}</td>
                        <td>{error.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="alert alert-info text-14">
          This endpoint returns <code>errorCode</code> null on its 404; only its
          request-validation failures carry a code (DPI-1000 / DPI-1006). Branch on
          the HTTP status and read <code>message</code>.
        </div>
      )}
    </div>
  );
}

export default ErrorCodes;
